import { useState } from "react";
import { UserName } from "@/components/badges/UserName";
import { useSentMentorshipRequests, MentorshipRequest } from "@/hooks/useMentorshipRequests";
import {
  useMentorshipRelationships,
  useRequestMentorshipAgain,
  MentorshipRelationship,
} from "@/hooks/useMentorshipRelationships";
import { useGetOrCreateMentorshipThread } from "@/hooks/useGetMentorshipThread";
import { useAuth } from "@/lib/auth";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from "@/components/ui/tooltip";
import { Loader2, Check, X, BookOpen, MessageSquare, Clock, Ban, CheckCircle, RefreshCw, Sparkles, Info, PartyPopper } from "lucide-react";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";
import { getStorageUrl } from "@/lib/storage";
import { CancelMentorshipRequestDialog } from "./CancelMentorshipRequestDialog";
import { CompleteMentorshipDialog } from "./CompleteMentorshipDialog";
import { RequestMentorshipAgainDialog } from "./RequestMentorshipAgainDialog";
import { toast } from "sonner";

export function SentMentorshipRequestsList() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: requests, isLoading, error: requestsError, refetch: refetchRequests } = useSentMentorshipRequests();
  const { data: relationships, isLoading: isLoadingRelationships, error: relationshipsError, refetch: refetchRelationships } = useMentorshipRelationships();
  
  // Filter relationships locally - single query, better performance
  const activeRelationships = relationships?.filter(r => r.status === "active") || [];
  const completedRelationships = relationships?.filter(r => r.status === "completed") || [];
  const getOrCreateThread = useGetOrCreateMentorshipThread();
  const requestAgain = useRequestMentorshipAgain();
  
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [requestToCancel, setRequestToCancel] = useState<MentorshipRequest | null>(null);
  const [completeMentorshipOpen, setCompleteMentorshipOpen] = useState(false);
  const [requestAgainOpen, setRequestAgainOpen] = useState(false);
  const [selectedRelationship, setSelectedRelationship] = useState<MentorshipRelationship | null>(null);

  const handleCancelClick = (request: MentorshipRequest) => {
    setRequestToCancel(request);
    setCancelDialogOpen(true);
  };

  const handleViewConversation = async (request: MentorshipRequest) => {
    try {
      let threadId = request.thread_id;

      if (!threadId) {
        threadId = await getOrCreateThread.mutateAsync(request.id);
      }

      if (!threadId) {
        toast.error("Could not open conversation - no thread available");
        return;
      }

      navigate(`/connections?threadId=${threadId}`);
    } catch (error) {
      console.error("[SentMentorshipRequestsList] handleViewConversation error:", error);
    }
  };

  const handleViewRelationshipConversation = (relationship: MentorshipRelationship) => {
    navigate(`/connections?threadId=${relationship.thread_id}`);
  };

  const handleCompleteMentorship = (relationship: MentorshipRelationship) => {
    setSelectedRelationship(relationship);
    setCompleteMentorshipOpen(true);
  };

  const handleRequestAgainClick = (relationship: MentorshipRelationship) => {
    setSelectedRelationship(relationship);
    setRequestAgainOpen(true);
  };

  const handleConfirmRequestAgain = async () => {
    if (!selectedRelationship) return;
    try {
      await requestAgain.mutateAsync(selectedRelationship.thread_id);
      setRequestAgainOpen(false);
      setSelectedRelationship(null);
    } catch (error) {
      // Error handled in mutation
    }
  };

  // Helper to get the "other person" for a relationship (from entrepreneur's perspective, show mentor)
  const getOtherPerson = (relationship: MentorshipRelationship) => {
    if (relationship.entrepreneur_id === user?.id) {
      return relationship.mentor;
    }
    return relationship.entrepreneur;
  };

  const getStatusBadge = (request: MentorshipRequest) => {
    switch (request.status) {
      case "pending":
        return (
          <Badge variant="outline" className="text-amber-600 border-amber-300">
            <Clock className="h-3 w-3 mr-1" />
            Pending
          </Badge>
        );
      case "accepted":
        return (
          <Badge variant="outline" className="text-green-600 border-green-300">
            <Check className="h-3 w-3 mr-1" />
            Accepted
          </Badge>
        );
      case "declined":
        return (
          <Badge variant="outline" className="text-red-600 border-red-300">
            <X className="h-3 w-3 mr-1" />
            Declined
          </Badge>
        );
      case "cancelled":
        return (
          <Badge variant="outline" className="text-muted-foreground border-muted">
            <Ban className="h-3 w-3 mr-1" />
            Cancelled
          </Badge>
        );
      default:
        return <Badge variant="outline">{request.status}</Badge>;
    }
  };

  const isLoadingAll = isLoading || isLoadingRelationships;

  // Only show error state if there's an actual error AND we don't have any data
  // This prevents showing errors when data is cached but a refetch fails
  const hasActualError = (requestsError && !requests) || (relationshipsError && !relationships);

  if (isLoadingAll) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (hasActualError) {
    console.error("[SentMentorshipRequestsList] Error loading data:", { requestsError, relationshipsError });
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <X className="h-12 w-12 text-destructive mb-4" />
          <p className="text-destructive font-medium">Unable to load mentorship data</p>
          <p className="text-sm text-muted-foreground text-center max-w-md mt-1">
            There was a problem loading your mentorship information. This may be a temporary issue.
          </p>
          <Button
            variant="outline"
            className="mt-4"
            onClick={() => {
              refetchRequests();
              refetchRelationships();
            }}
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Try Again
          </Button>
        </CardContent>
      </Card>
    );
  }

  const pendingRequests = requests?.filter((r) => r.status === "pending") || [];
  const pastMentorshipRequests = requests?.filter((r) => r.status !== "pending") || [];

  const hasNoContent =
    (!requests || requests.length === 0) &&
    (!activeRelationships || activeRelationships.length === 0) &&
    (!completedRelationships || completedRelationships.length === 0);

  if (hasNoContent) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <BookOpen className="h-12 w-12 text-muted-foreground mb-4" />
          <p className="text-muted-foreground font-medium">No mentorship requests yet</p>
          <p className="text-sm text-muted-foreground text-center max-w-md mt-1">
            When you request mentorship from mentors, your requests will appear here.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <TooltipProvider>
      <div className="space-y-6">
        {/* Educational message about mentorship cycles */}
        <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Sparkles className="h-5 w-5 text-primary mt-0.5 shrink-0" />
            <div className="space-y-1">
              <p className="text-sm font-medium">
                Mentorships are short, focused cycles designed to help you progress your business quickly.
              </p>
              <p className="text-sm text-muted-foreground">
                Each cycle gives you guidance and access to resources. When you complete a cycle, you can always start another!
              </p>
            </div>
          </div>
        </div>

        {/* Pending Requests */}
        {pendingRequests.length > 0 && (
          <div className="space-y-3">
            <h3 className="font-semibold text-lg">
              Pending Requests ({pendingRequests.length})
            </h3>
            {pendingRequests.map((request) => {
              const mentor = Array.isArray(request.mentor) ? request.mentor[0] : request.mentor;
              const topic = Array.isArray(request.topic) ? request.topic[0] : request.topic;

              return (
                <Card key={request.id}>
                  <CardContent className="p-4">
                    <div className="flex items-start gap-4">
                      <Avatar className="h-12 w-12">
                        <AvatarImage src={getStorageUrl("avatars", mentor?.avatar_url) || undefined} />
                        <AvatarFallback className="bg-primary text-primary-foreground">
                          {mentor?.full_name?.charAt(0)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-medium"><UserName userId={mentor?.id} name={mentor?.full_name} /></p>
                          {getStatusBadge(request)}
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="secondary" className="text-xs">
                            <BookOpen className="h-3 w-3 mr-1" />
                            {topic?.name || "Unknown Topic"}
                          </Badge>
                          <span className="text-xs text-muted-foreground">
                            {format(new Date(request.created_at), "MMM d, yyyy")}
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                          {request.message}
                        </p>
                        <div className="flex gap-2 mt-3">
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-destructive hover:text-destructive"
                            onClick={() => handleCancelClick(request)}
                          >
                            <Ban className="h-4 w-4 mr-1" />
                            Cancel Request
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Active Mentorship Cycles */}
        {activeRelationships && activeRelationships.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-lg flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-primary" />
                Active Mentorship Cycles ({activeRelationships.length})
              </h3>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  <p>Mentorships are short, focused cycles designed to help you move forward quickly.</p>
                </TooltipContent>
              </Tooltip>
            </div>
            {activeRelationships.map((relationship) => {
              const otherPerson = getOtherPerson(relationship);

              return (
                <Card key={relationship.id} className="border-primary/20 bg-primary/5">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-4">
                      <Avatar className="h-12 w-12">
                        <AvatarImage src={getStorageUrl("avatars", otherPerson?.avatar_url) || undefined} />
                        <AvatarFallback className="bg-primary text-primary-foreground">
                          {otherPerson?.full_name?.charAt(0)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-medium"><UserName userId={otherPerson?.id} name={otherPerson?.full_name} /></p>
                          <Badge className="bg-primary/20 text-primary border-primary/30">
                            <Sparkles className="h-3 w-3 mr-1" />
                            Active Mentorship Cycle
                          </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          Started {format(new Date(relationship.started_at), "MMM d, yyyy")}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1 italic">
                          Mentorships are designed to be short, focused cycles.
                        </p>
                        <div className="flex gap-2 mt-3 flex-wrap">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleViewRelationshipConversation(relationship)}
                          >
                            <MessageSquare className="h-4 w-4 mr-1" />
                            Message
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleCompleteMentorship(relationship)}
                          >
                            <CheckCircle className="h-4 w-4 mr-1" />
                            Complete Mentorship
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Completed Mentorships */}
        {completedRelationships && completedRelationships.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-lg flex items-center gap-2">
                <PartyPopper className="h-5 w-5 text-green-600" />
                Completed Mentorships
              </h3>
            </div>
            {completedRelationships.map((relationship) => {
              const otherPerson = getOtherPerson(relationship);

              return (
                <Card key={relationship.id}>
                  <CardContent className="p-4">
                    <div className="flex items-start gap-4">
                      <Avatar className="h-10 w-10">
                        <AvatarImage src={getStorageUrl("avatars", otherPerson?.avatar_url) || undefined} />
                        <AvatarFallback className="bg-muted text-muted-foreground">
                          {otherPerson?.full_name?.charAt(0)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-medium"><UserName userId={otherPerson?.id} name={otherPerson?.full_name} /></p>
                          <Badge variant="secondary" className="bg-green-100 text-green-700 border-green-200">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Completed
                          </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          Completed {relationship.completed_at ? format(new Date(relationship.completed_at), "MMM d, yyyy") : ""}
                        </p>
                        {relationship.completion_note && (
                          <p className="text-sm text-muted-foreground mt-2 italic">
                            "{relationship.completion_note}"
                          </p>
                        )}
                        <div className="flex gap-2 mt-3 flex-wrap">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleViewRelationshipConversation(relationship)}
                          >
                            <MessageSquare className="h-4 w-4 mr-1" />
                            View Conversation
                          </Button>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleRequestAgainClick(relationship)}
                                disabled={requestAgain.isPending}
                              >
                                {requestAgain.isPending ? (
                                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                                ) : (
                                  <RefreshCw className="h-4 w-4 mr-1" />
                                )}
                                Request Mentorship Again
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>When you're ready for your next focused push.</p>
                            </TooltipContent>
                          </Tooltip>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Past Mentorship Requests (from mentorship_requests table) */}
        {pastMentorshipRequests.length > 0 && (
          <div className="space-y-3">
            <h3 className="font-semibold text-lg">Past Requests</h3>
            {pastMentorshipRequests.map((request) => {
              const mentor = Array.isArray(request.mentor) ? request.mentor[0] : request.mentor;
              const topic = Array.isArray(request.topic) ? request.topic[0] : request.topic;

              return (
                <Card key={request.id} className="opacity-75">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-4">
                      <Avatar className="h-10 w-10">
                        <AvatarImage src={getStorageUrl("avatars", mentor?.avatar_url) || undefined} />
                        <AvatarFallback className="bg-muted text-muted-foreground">
                          {mentor?.full_name?.charAt(0)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-medium"><UserName userId={mentor?.id} name={mentor?.full_name} /></p>
                          {getStatusBadge(request)}
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="secondary" className="text-xs">
                            {topic?.name || "Unknown Topic"}
                          </Badge>
                          <span className="text-xs text-muted-foreground">
                            {format(new Date(request.created_at), "MMM d, yyyy")}
                          </span>
                        </div>
                        {request.status === "cancelled" && request.cancelled_at && (
                          <p className="text-xs text-muted-foreground mt-2">
                            Cancelled on {format(new Date(request.cancelled_at), "MMM d, yyyy")}
                            {request.cancellation_actor === "admin" && " by administrator"}
                          </p>
                        )}
                        {request.status === "accepted" && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="mt-2"
                            onClick={() => handleViewConversation(request)}
                            disabled={getOrCreateThread.isPending}
                          >
                            {getOrCreateThread.isPending ? (
                              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                            ) : (
                              <MessageSquare className="h-4 w-4 mr-1" />
                            )}
                            View Conversation
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Cancel Dialog */}
        {requestToCancel && (
          <CancelMentorshipRequestDialog
            open={cancelDialogOpen}
            onOpenChange={setCancelDialogOpen}
            requestId={requestToCancel.id}
            mentorName={
              (Array.isArray(requestToCancel.mentor)
                ? requestToCancel.mentor[0]
                : requestToCancel.mentor
              )?.full_name
            }
          />
        )}

        {/* Complete Mentorship Dialog */}
        {selectedRelationship && (
          <CompleteMentorshipDialog
            open={completeMentorshipOpen}
            onOpenChange={setCompleteMentorshipOpen}
            threadId={selectedRelationship.thread_id}
            otherPersonName={getOtherPerson(selectedRelationship)?.full_name || ""}
          />
        )}

        {/* Request Mentorship Again Dialog */}
        {selectedRelationship && (
          <RequestMentorshipAgainDialog
            open={requestAgainOpen}
            onOpenChange={setRequestAgainOpen}
            onConfirm={handleConfirmRequestAgain}
            isPending={requestAgain.isPending}
            otherPersonName={getOtherPerson(selectedRelationship)?.full_name || ""}
          />
        )}
      </div>
    </TooltipProvider>
  );
}
